import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { useSelector } from 'react-redux'
import qs from 'query-string'
import isNil from 'lodash/isNil'

import { logEvent } from 'lib/analytics'
import { Group, Post } from 'lib/_types'
import { ROUTE_PATH } from 'lib/paths'
import { matchI18nPathList, useMatch, useMatchList } from 'features/i18n/router'
import { useCurrentUser } from 'store/api/yay/user'

import PostReported from '../PostReported'

export const useIsGroupAdmin = ({
  group,
}: {
  group: {
    data?: Group
  }
}) => {
  const { data: currentUser } = useCurrentUser()

  // https://jira.nanameue.jp/browse/YAY-7503
  const isGroupAdmin = useMemo(() => {
    if (isNil(group?.data?.owner?.id)) return false

    // If current user is owner or moderator of group then inappropriate content can be shown
    return [group?.data?.owner?.id]
      .concat(group?.data?.moderator_ids || [])
      .includes(currentUser?.id)
  }, [currentUser, group])

  return isGroupAdmin
}

export const usePostLink = ({
  noLink,
  post,
}: {
  noLink: boolean
  post: Post
}): {
  postLink?: string
  linkRoute?: string | Function
} => {
  if (noLink) return {}

  const isGroupPost = !isNil(post.group_id)
  const isThreadPost = !isNil(post?.thread_id)
  const isConversationPost =
    !isNil(post?.conversation_id) || !isNil(post?.in_reply_to_post)

  const isConversationPage = useMatchList([
    ROUTE_PATH.CONVERSATION(),
    ROUTE_PATH.GROUP_CONVERSATION(),
    ROUTE_PATH.GROUP_THREAD_CONVERSATION(),
  ])
  const isGroupHighlightPage = useMatch(ROUTE_PATH.GROUP_HIGHLIGHTS())

  const getLink = () => {
    if (isThreadPost) {
      if (isConversationPost && !isConversationPage) {
        return {
          postLink: ROUTE_PATH.GROUP_THREAD_CONVERSATION(
            post.group_id,
            post.thread_id,
            post.conversation_id
          ),
          linkRoute: ROUTE_PATH.GROUP_THREAD_CONVERSATION,
        }
      }

      return {
        postLink: ROUTE_PATH.GROUP_THREAD_POST(
          post.group_id,
          post.thread_id,
          post.id
        ),
        linkRoute: ROUTE_PATH.GROUP_THREAD_POST,
      }
    } else if (isConversationPost && !isConversationPage) {
      if (isGroupPost) {
        return {
          postLink: ROUTE_PATH.GROUP_CONVERSATION(
            post.group_id,
            post.conversation_id
          ),
          linkRoute: ROUTE_PATH.GROUP_CONVERSATION,
        }
      }

      return {
        postLink: ROUTE_PATH.CONVERSATION(post.conversation_id),
        linkRoute: ROUTE_PATH.CONVERSATION,
      }
    } else {
      if (isGroupPost) {
        return {
          postLink: ROUTE_PATH.GROUP_POST(post.group_id, post.id),
          linkRoute: ROUTE_PATH.GROUP_POST,
        }
      }

      return { postLink: ROUTE_PATH.POST(post.id), linkRoute: ROUTE_PATH.POST }
    }
  }

  const link = getLink()

  // related to forceActive in GroupNav
  const postFix = isGroupHighlightPage ? '?h=highlight' : ''

  if (postFix) {
    link.postLink += postFix
  }

  return link
}

export const useScrollToPost = ({ post, rootRef }) => {
  const location = useLocation()
  const goToPostId = qs.parse(location.search)?.go_to_post_id || ''

  useEffect(() => {
    if (post.id === Number(goToPostId)) {
      rootRef.current.scrollIntoView()
    }
  }, [])

  return {
    rootRef,
  }
}

export const usePostReported = ({
  post,
  forceShowInappropriateContent = false,
  mayShowInappropriateContent = false,
}: {
  post: Post
  forceShowInappropriateContent?: boolean // if true, show the reported post
  mayShowInappropriateContent?: boolean // if true, render the button to show reported post
}) => {
  const currentUserId = useCurrentUser().data?.id
  const [isReported, isReportedSet] = useState((post?.reported_count || 0) >= 3)

  if (
    currentUserId !== post?.user?.id &&
    isReported &&
    !forceShowInappropriateContent
  ) {
    return (
      <PostReported
        onClick={() => {
          if (mayShowInappropriateContent) {
            isReportedSet(false)
          }
        }}
        mayShowInappropriateContent={mayShowInappropriateContent}
      />
    )
  }
}

export const useTrackPostViewEvent = ({ post, rootRef }) => {
  let startVisibleTimestamp: any
  const { pathname } = useLocation()
  const currentUserId = useCurrentUser().data?.id

  const fromTimeline = useMemo(() => {
    switch (true) {
      case matchI18nPathList([ROUTE_PATH.TIMELINE_PUBLIC]):
        return 'timeline'

      case matchI18nPathList([ROUTE_PATH.TIMELINE_HASHTAG()]):
        return 'tags'

      case matchI18nPathList(
        [
          ROUTE_PATH.POST(),
          ROUTE_PATH.CONVERSATION(),
          ROUTE_PATH.GROUP_CONVERSATION(),
          ROUTE_PATH.GROUP_THREAD_CONVERSATION(),
        ],
        { startsWith: true }
      ):
        return 'conversations'

      case matchI18nPathList([ROUTE_PATH.GROUP()], { startsWith: true }):
        return 'group_timeline'

      case matchI18nPathList([ROUTE_PATH.USER(), ROUTE_PATH.BOOKMARKS], {
        startsWith: true,
      }):
        return currentUserId === post?.user?.id
          ? 'user_timeline_owner'
          : 'user_timeline_other'

      default:
        return 'following_timeline'
    }
  }, [pathname, post, currentUserId])

  const logPostView = useCallback(() => {
    if (!startVisibleTimestamp) return

    const diffTimeInSeconds = (performance.now() - startVisibleTimestamp) / 1000

    if (diffTimeInSeconds >= 3) {
      logEvent.post_view({
        target_post_id: post?.id,
        target_user_id: post?.user?.id,
        duration_sec: diffTimeInSeconds.toFixed(3),
        from_timeline: fromTimeline,
        is_hot: null,
      })
    }
  }, [post, rootRef, fromTimeline])

  const observer = new IntersectionObserver(
    (elements) => {
      const { isIntersecting } = elements[0]

      if (isIntersecting) {
        startVisibleTimestamp = performance.now()
      } else {
        logPostView()
      }
    },
    {
      threshold: 1,
    }
  )

  useEffect(() => {
    observer.observe(rootRef.current)

    return () => {
      logPostView()
      observer.disconnect()
    }
  }, [])
}
